<script lang="ts">
  /* «План периода»: the owner sets each calendar month's plan and may change
     it while the month runs. Quarters, half-years and the year add up their
     months. An ended month is closed by the system — plan and result are
     recorded and shown read-only. Backend: apps/tenants/dashboard_api.py. */
  import { untrack } from "svelte";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import { _ } from "svelte-i18n";
  import LoadErrorState from "$components/LoadErrorState.svelte";
  import { runLoad, type LoadStatus } from "$lib/loadState";
  import { calendarIsoDate, companyCalendarDate } from "$lib/format";
  import {
    METRICS,
    METRIC_LABEL_KEYS,
    MONEY_METRICS,
    fetchDashboardPlan,
    formatFigure,
    monthName,
    saveDashboardPlan,
    type DashboardPlan,
    type Metric,
    type PlanMonth,
  } from "$lib/dashboard";

  let { slug, onchanged }: { slug: string; onchanged?: () => void } = $props();

  const todayIso = untrack(() => calendarIsoDate(companyCalendarDate(slug)));
  const currentYear = Number(todayIso.slice(0, 4));
  let year = $state(currentYear);
  let plan = $state<DashboardPlan | null>(null);
  let status = $state<LoadStatus>("loading");
  let selected = $state(todayIso.slice(0, 7));
  let draft = $state<Record<Metric, string>>(blankDraft());
  let prefilled = $state(false);
  let saving = $state(false);
  let saved = $state(false);
  let error = $state("");

  const month = $derived(plan?.months.find((m) => m.month === selected) ?? null);
  const canEdit = $derived(Boolean(plan?.can_edit && month?.editable));

  function blankDraft(): Record<Metric, string> {
    return Object.fromEntries(METRICS.map((m) => [m, ""])) as Record<Metric, string>;
  }

  function draftFrom(targets: Record<Metric, number | null> | undefined): Record<Metric, string> {
    return Object.fromEntries(
      METRICS.map((m) => [m, targets?.[m] != null ? String(targets[m]) : ""]),
    ) as Record<Metric, string>;
  }

  function hasTargets(m: PlanMonth | null | undefined): boolean {
    return Boolean(m && METRICS.some((k) => m.targets[k] != null));
  }

  /** An open month with no plan yet starts from the previous month's figures;
   *  nothing is stored until the owner saves. */
  function selectMonth(m: PlanMonth) {
    selected = m.month;
    saved = false;
    error = "";
    const index = plan?.months.indexOf(m) ?? -1;
    const previous = index > 0 ? plan?.months[index - 1] : null;
    prefilled = Boolean(plan?.can_edit && m.editable && !hasTargets(m) && hasTargets(previous));
    draft = draftFrom(prefilled ? previous?.targets : m.targets);
  }

  function load() {
    runLoad(
      (s) => (status = s),
      async () => {
        plan = await fetchDashboardPlan(slug, year);
        const keep = plan.months.find((m) => m.month === selected);
        selectMonth(keep ?? plan.months.find((m) => m.month === plan?.current_month) ?? plan.months[0]);
      },
    );
  }

  $effect(() => {
    void [slug, year];
    load();
  });

  function parse(text: string): number | null {
    const clean = text.trim().replace(",", ".");
    if (clean === "") return null;
    const n = Number(clean);
    return Number.isFinite(n) ? n : NaN;
  }

  async function save() {
    if (!plan || !month) return;
    const targets = {} as Record<Metric, number | null>;
    for (const m of METRICS) {
      const n = parse(draft[m]);
      if (n !== null && (Number.isNaN(n) || n < 0)) {
        error = $_("dashboard.plan.invalid", { values: { metric: $_(METRIC_LABEL_KEYS[m]) } });
        return;
      }
      targets[m] = n;
    }
    saving = true;
    error = "";
    try {
      const updated = await saveDashboardPlan(slug, month.month, targets);
      plan.months = plan.months.map((m) =>
        m.month === updated.month ? { ...updated, actuals: m.actuals } : m,
      );
      prefilled = false;
      saved = true;
      onchanged?.();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      saving = false;
    }
  }

  function goalOf(m: Metric): number | null {
    if (canEdit) {
      const n = parse(draft[m]);
      return n === null || Number.isNaN(n) ? null : n;
    }
    return month?.targets[m] ?? null;
  }

  function pctOf(actual: number | null | undefined, goal: number | null): number | null {
    return goal && actual != null ? (actual / goal) * 100 : null;
  }

  /** Quarter, half-year and year columns: the sum of their months. */
  const SUMMARY = [
    { key: "q1", months: [0, 1, 2], labelKey: "dashboard.plan.quarter", n: 1 },
    { key: "q2", months: [3, 4, 5], labelKey: "dashboard.plan.quarter", n: 2 },
    { key: "q3", months: [6, 7, 8], labelKey: "dashboard.plan.quarter", n: 3 },
    { key: "q4", months: [9, 10, 11], labelKey: "dashboard.plan.quarter", n: 4 },
    { key: "h1", months: [0, 1, 2, 3, 4, 5], labelKey: "dashboard.plan.half", n: 1 },
    { key: "h2", months: [6, 7, 8, 9, 10, 11], labelKey: "dashboard.plan.half", n: 2 },
    { key: "year", months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], labelKey: "dashboard.plan.yearTotal", n: 0 },
  ];

  function aggregate(metric: Metric, indexes: number[]): { goal: number | null; actual: number | null } {
    let goal: number | null = null;
    let actual: number | null = null;
    for (const i of indexes) {
      const m = plan?.months[i];
      const t = m?.targets[metric];
      const a = m?.actuals?.[metric];
      if (t != null) goal = (goal ?? 0) + t;
      if (a != null) actual = (actual ?? 0) + a;
    }
    return { goal, actual };
  }

  function tone(pct: number | null): string {
    if (pct == null) return "none";
    if (pct >= 100) return "met";
    if (pct >= 50) return "mid";
    return "low";
  }
</script>

<div class="plan">
  <div class="card plan-card">
    <div class="plan-head">
      <div>
        <h3 class="plan-title">{$_("dashboard.plan.title")}</h3>
        <p class="plan-hint">{$_("dashboard.plan.hint")}</p>
      </div>
      <div class="year-nav">
        <button type="button" class="icon-btn" onclick={() => (year -= 1)} title={$_("dashboard.plan.prevYear")} aria-label={$_("dashboard.plan.prevYear")}>
          <span class="material-icons-outlined" aria-hidden="true">chevron_left</span>
        </button>
        <span class="year-label">{year}</span>
        <button type="button" class="icon-btn" onclick={() => (year += 1)} disabled={year > currentYear} title={$_("dashboard.plan.nextYear")} aria-label={$_("dashboard.plan.nextYear")}>
          <span class="material-icons-outlined" aria-hidden="true">chevron_right</span>
        </button>
      </div>
    </div>

    {#if status === "error"}
      <LoadErrorState onretry={load} />
    {:else if !plan}
      <p class="plan-note">{$_("common.loading")}</p>
    {:else}
      <div class="month-chips" role="tablist">
        {#each plan.months as m (m.month)}
          <button
            type="button"
            role="tab"
            class="month-chip"
            class:on={m.month === selected}
            class:current={m.month === plan.current_month}
            class:planned={hasTargets(m)}
            aria-selected={m.month === selected}
            onclick={() => selectMonth(m)}
          >
            {#if m.closed}<span class="material-icons-outlined chip-lock" aria-hidden="true">lock</span>{/if}
            {monthName(m.month)}
          </button>
        {/each}
      </div>

      {#if month}
        <p class="plan-note" class:plan-note--closed={month.closed}>
          {#if month.closed}
            {$_("dashboard.plan.closed")}
          {:else if !plan.can_edit}
            {$_("dashboard.plan.readOnly")}
          {:else if month.month === plan.current_month}
            {$_("dashboard.plan.open")}
          {:else}
            {$_("dashboard.plan.future")}
          {/if}
        </p>
        {#if prefilled}<p class="plan-note plan-note--prefill">{$_("dashboard.plan.prefilled")}</p>{/if}

        <div class="table-scroll">
          <table use:resizableColumns={"dashboard-plan"} class="plan-table">
            <thead>
              <tr>
                <th>{$_("dashboard.plan.metric")}</th>
                <th class="num">{$_("dashboard.plan.target")}</th>
                <th class="num">{$_("dashboard.plan.actual")}</th>
                <th class="num">{$_("dashboard.plan.progress")}</th>
              </tr>
            </thead>
            <tbody>
              {#each METRICS as m (m)}
                {@const goal = goalOf(m)}
                {@const actual = month.actuals?.[m] ?? null}
                {@const pct = pctOf(actual, goal)}
                <tr>
                  <td>{$_(METRIC_LABEL_KEYS[m])}</td>
                  <td class="num">
                    {#if canEdit}
                      <input
                        class="plan-input"
                        type="text"
                        inputmode={MONEY_METRICS.includes(m) ? "decimal" : "numeric"}
                        bind:value={draft[m]}
                        oninput={() => (saved = false)}
                        aria-label={$_(METRIC_LABEL_KEYS[m])}
                        placeholder="-"
                      />
                    {:else}
                      {goal != null ? formatFigure(m, goal) : "-"}
                    {/if}
                  </td>
                  <td class="num">{actual != null ? formatFigure(m, actual) : "-"}</td>
                  <td class="num">
                    <span class="progress">
                      <span class="progress-track"><span class="progress-fill tone--{tone(pct)}" style="width:{Math.min(100, pct ?? 0)}%"></span></span>
                      <span class="progress-pct">{pct != null ? `${Math.round(pct)}%` : "-"}</span>
                    </span>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>

        {#if canEdit}
          <div class="plan-actions">
            {#if error}<span class="plan-error">{error}</span>{/if}
            {#if saved}<span class="plan-saved">{$_("dashboard.plan.saved")}</span>{/if}
            <button type="button" class="btn btn--primary" onclick={save} disabled={saving}>{$_("dashboard.plan.save")}</button>
          </div>
        {/if}
      {/if}
    {/if}
  </div>

  {#if plan}
    <div class="card plan-card">
      <h3 class="plan-title">{$_("dashboard.plan.summary", { values: { year } })}</h3>
      <p class="plan-hint">{$_("dashboard.plan.summaryHint")}</p>
      <div class="table-scroll">
        <table use:resizableColumns={"dashboard-plan-summary"} class="plan-table summary-table">
          <thead>
            <tr>
              <th>{$_("dashboard.plan.metric")}</th>
              {#each SUMMARY as col (col.key)}
                <th class="num">{$_(col.labelKey, { values: { n: col.n } })}</th>
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each METRICS as m (m)}
              <tr>
                <td>{$_(METRIC_LABEL_KEYS[m])}</td>
                {#each SUMMARY as col (col.key)}
                  {@const cell = aggregate(m, col.months)}
                  {@const pct = pctOf(cell.actual, cell.goal)}
                  <td class="num">
                    <span class="cell-main">{cell.actual != null ? formatFigure(m, cell.actual, true) : "-"} / {cell.goal != null ? formatFigure(m, cell.goal, true) : "-"}</span>
                    {#if pct != null}<span class="cell-pct tone-text--{tone(pct)}">{Math.round(pct)}%</span>{/if}
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<style>
  .plan { display: flex; flex-direction: column; gap: 16px; }
  .plan-card { padding: 20px 24px; display: flex; flex-direction: column; gap: 12px; min-width: 0; }
  .plan-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; }
  .plan-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--color-surface-darker); }
  .plan-hint { margin: 4px 0 0; font-size: 12.5px; line-height: 1.45; color: var(--color-text-muted); max-width: 760px; }
  .year-nav { display: inline-flex; align-items: center; gap: 4px; }
  .year-label { min-width: 48px; text-align: center; font-weight: 700; color: var(--color-surface-darker); }
  .icon-btn {
    display: inline-grid; place-items: center; width: 32px; height: 32px; padding: 0;
    border: 1px solid var(--color-divider); border-radius: 7px; background: #fff;
    color: var(--color-surface-darker); cursor: pointer;
  }
  .icon-btn:disabled { opacity: 0.4; cursor: default; }
  .icon-btn .material-icons-outlined { font-size: 20px; }

  .month-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .month-chip {
    display: inline-flex; align-items: center; gap: 3px;
    padding: 5px 11px; border: 1px solid var(--color-divider); border-radius: 14px;
    background: #fff; font: inherit; font-size: 12.5px; color: var(--color-text-muted); cursor: pointer;
  }
  .month-chip.planned { color: var(--color-surface-darker); font-weight: 600; }
  .month-chip.current { border-color: var(--color-primary); }
  .month-chip.on { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }
  .chip-lock { font-size: 13px; }

  .plan-note { margin: 0; font-size: 12.5px; color: var(--color-text-muted); }
  .plan-note--closed { color: var(--color-surface-darker); }
  .plan-note--prefill { color: #e65100; }

  .table-scroll { overflow-x: auto; }
  .plan-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .plan-table th {
    padding: 6px 10px; text-align: left; font-size: 11px; font-weight: 600;
    color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.04em;
    border-bottom: 1px solid var(--color-divider); white-space: nowrap;
  }
  .plan-table td { padding: 7px 10px; border-bottom: 1px solid var(--color-divider); vertical-align: middle; }
  .plan-table tr:last-child td { border-bottom: none; }
  .plan-table th.num, .plan-table td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .plan-input {
    width: 120px; padding: 6px 8px; text-align: right; font: inherit; font-size: 13px;
    border: 1px solid var(--color-divider); border-radius: 6px;
  }
  .plan-input:focus { outline: none; border-color: var(--color-primary); }

  .progress { display: inline-flex; align-items: center; gap: 8px; }
  .progress-track { width: 90px; height: 8px; border-radius: 4px; background: var(--color-divider); overflow: hidden; }
  .progress-fill { display: block; height: 100%; border-radius: 4px; }
  .progress-pct { min-width: 40px; }
  .tone--low { background: #e65100; }
  .tone--mid { background: var(--color-primary); }
  .tone--met { background: #2e7d32; }
  .tone--none { background: transparent; }

  .plan-actions { display: flex; justify-content: flex-end; align-items: center; gap: 12px; flex-wrap: wrap; }
  .plan-error { font-size: 12.5px; color: #c62828; }
  .plan-saved { font-size: 12.5px; color: #2e7d32; }

  .summary-table td.num { line-height: 1.25; }
  .cell-main { display: block; }
  .cell-pct { display: block; font-size: 11px; font-weight: 600; }
  .tone-text--low { color: #e65100; }
  .tone-text--mid { color: var(--color-primary); }
  .tone-text--met { color: #2e7d32; }
</style>
